import type { User } from './types';

/**
 * Auth client for the Go backend (Plan 2):
 *   POST /api/login     {username, password}        -> 200 + cookies | 401
 *   POST /api/logout    X-CSRF-Token header         -> 204
 *   GET  /api/me                                    -> 200 user | 401
 *
 * Mock mode (`VITE_USE_MOCKS=1`) auto-logs the SPA in as a local "dev" user
 * with role=owner so the dashboard renders without a backend; this is a
 * build-time check, so production bundles never see it.
 *
 * Cookies the Go backend sets:
 *   pacebi_session  HttpOnly  — read+verified by the server only
 *   pacebi_csrf     readable  — JS reads it and echoes it back via X-CSRF-Token
 *                               on state-changing requests (logout).
 */

const mockDevUser: User = { username: 'dev', role: 'owner', sid_assigned: null };

// Evaluated per-call so vi.stubEnv in tests works; production builds
// dead-code-eliminate the body of each `if (isMock())` branch when the
// env var isn't set.
function isMock(): boolean {
  return import.meta.env.VITE_USE_MOCKS === '1';
}

function readCookie(name: string): string | null {
  const m = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
  return m ? decodeURIComponent(m[1]) : null;
}

export async function login(username: string, password: string): Promise<User | null> {
  if (isMock()) return mockDevUser;
  const res = await fetch('/api/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ username, password }),
  });
  if (res.status === 401) return null;
  if (!res.ok) throw new Error(`login failed: ${res.status}`);
  return (await res.json()) as User;
}

export async function logout(): Promise<void> {
  if (isMock()) return;
  const csrf = readCookie('pacebi_csrf');
  await fetch('/api/logout', {
    method: 'POST',
    headers: csrf ? { 'X-CSRF-Token': csrf } : {},
    credentials: 'same-origin',
  });
}

export async function currentUser(): Promise<User | null> {
  if (isMock()) return mockDevUser;
  const res = await fetch('/api/me', { credentials: 'same-origin' });
  if (res.status === 401) return null;
  if (!res.ok) throw new Error(`me failed: ${res.status}`);
  return (await res.json()) as User;
}
